import { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Plus, ListChecks, RefreshCw } from "lucide-react";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import Banner from "@/components/Banner";

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

export default function LeaveTypes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    code: "",
    annual_quota: 12,
    paid: true,
    color: "#226DB4",
  });
  const [err, setErr] = useState("");

  const { items: sortedItems, sortBy, sortDir, requestSort } = useSortableData(items, { initialKey: "code" });

  const load = () =>
    api.get("/leave-types").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/leave-types", { ...form, code: form.code.toUpperCase() });
      setOpen(false);
      setForm({ name: "", code: "", annual_quota: 12, paid: true, color: "#226DB4" });
      load();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed");
    }
  };

  return (
    <AdminLayout title="Leave Types" breadcrumbs={["Home", "Organization", "Leave Types"]}>
      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800">All Leave Types</h3>
          <div className="flex items-center gap-2">
            <button onClick={load} title="Refresh" data-testid="lt-refresh-btn" className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              data-testid="add-leave-type-btn"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded font-medium"
            >
              <Plus className="h-4 w-4" /> Add Leave Type
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortableTH columnKey="name" active={sortBy} dir={sortDir} onClick={requestSort}>Name</SortableTH>
              <SortableTH columnKey="code" active={sortBy} dir={sortDir} onClick={requestSort}>Code</SortableTH>
              <SortableTH columnKey="annual_quota" active={sortBy} dir={sortDir} onClick={requestSort}>Annual Quota</SortableTH>
              <SortableTH columnKey="paid" active={sortBy} dir={sortDir} onClick={requestSort}>Paid?</SortableTH>
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 ? (
              <EmptyState colSpan={4} message="No leave types defined yet" />
            ) : sortedItems.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 rounded-full"
                    style={{ backgroundColor: l.color }}
                  />
                  {l.name}
                </td>
                <td className="px-4 py-3 text-slate-600">{l.code}</td>
                <td className="px-4 py-3 text-slate-600">{l.annual_quota} days</td>
                <td className="px-4 py-3">
                  <span
                    className={`text-[11px] px-2 py-1 rounded border font-semibold ${
                      l.paid
                        ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"
                    }`}
                  >
                    {l.paid ? "Paid" : "Unpaid"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={submit}
            className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4"
          >
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-[#226DB4]" /> New Leave Type
            </h3>
            <input
              required
              placeholder="Name (e.g. Maternity Leave)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
              data-testid="lt-name-input"
            />
            <input
              required
              placeholder="Code (e.g. ML)"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className={inputCls}
              data-testid="lt-code-input"
            />
            <input
              type="number"
              min={0}
              placeholder="Annual Quota (days)"
              value={form.annual_quota}
              onChange={(e) =>
                setForm({ ...form, annual_quota: parseFloat(e.target.value) || 0 })
              }
              className={inputCls}
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.paid}
                onChange={(e) => setForm({ ...form, paid: e.target.checked })}
              />
              Paid leave (deducts from balance)
            </label>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">
                Color
              </label>
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-9 w-16 border border-slate-200 rounded"
              />
            </div>
            <Banner variant="error">{err}</Banner>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm border border-slate-200 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded"
                data-testid="lt-save-btn"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
